/**
 * Athlete App - Highlights Page
 * Create and manage highlight reels with clips
 *
 * Paywall Features:
 * - AI Auto-Detect Highlights: Requires ATHLETE_BASIC+
 * - Publish/Share Reels: Requires ATHLETE_BASIC+
 */

import { useState, useEffect } from 'react';
import {
  listHighlightReels,
  createHighlightReel,
  publishHighlightReel,
  deleteHighlightReel,
  getHighlightReel,
  listVideos,
  listVideoClips,
  createClip,
  addClipToReel,
  removeClipFromReel,
  aiDetectHighlights,
  listTeams,
  useUser,
  type Organization,
  type HighlightReel,
  type Video,
  type VideoClip,
  type Team,
} from '@tss/shared';
import { TeamLeaderboard } from '../components/Leaderboard/TeamLeaderboard';
import {
  Film,
  Plus,
  Share2,
  Scissors,
  X,
  Loader2,
  Check,
  Copy,
  Trash2,
  Video as VideoIcon,
  Sparkles,
  Play,
  Lock,
  Crown,
} from 'lucide-react';
import { formatDuration } from '@tss/shared';
import { Button } from '@tss/shared/ui';
import {
  PageHeader,
  TileGrid,
  HighlightTile,
  EmptyState,
  Skeleton,
  PaywallOverlay,
  PremiumBadge,
} from '../components/UI';
import { useSubscription } from '../hooks/useSubscription';

interface HighlightsPageProps {
  org: Organization | null;
  proxyAthleteId?: string;
}

export function HighlightsPage({ org, proxyAthleteId }: HighlightsPageProps) {
  const [reels, setReels] = useState<HighlightReel[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedReel, setSelectedReel] = useState<HighlightReel | null>(null);
  const [showClipCreator, setShowClipCreator] = useState(false);
  const [showAIPaywall, setShowAIPaywall] = useState(false);
  const [primaryTeam, setPrimaryTeam] = useState<Team | null>(null);

  const { userId } = useUser();

  // Subscription for paywalls
  const { canAccess } = useSubscription();
  const canUseAI = canAccess('ai_highlights');
  const canPublish = canAccess('publish_reels');

  useEffect(() => {
    if (org) {
      loadReels();
      loadPrimaryTeam();
    } else {
      setLoading(false);
    }
  }, [org, proxyAthleteId]);

  async function loadPrimaryTeam() {
    if (!org) return;
    const athleteId = proxyAthleteId || userId || undefined;
    try {
      const { teams } = await listTeams(org.org_id, athleteId);
      setPrimaryTeam(teams && teams.length > 0 ? teams[0] : null);
    } catch (err) {
      // Leaderboard is non-critical — silently degrade
      console.error('Failed to load athlete teams for leaderboard:', err);
      setPrimaryTeam(null);
    }
  }

  async function loadReels() {
    if (!org) return;
    setLoading(true);
    try {
      const { reels: reelList } = await listHighlightReels(org.org_id);
      setReels(reelList || []);
    } catch (err) {
      console.error('Failed to load reels:', err);
    } finally {
      setLoading(false);
    }
  }

  function handleCreateComplete() {
    setShowCreateModal(false);
    loadReels();
  }

  function handleReelUpdate() {
    loadReels();
    if (selectedReel) {
      // Reload the selected reel
      getHighlightReel(selectedReel.reel_id)
        .then(({ reel }) => {
          setSelectedReel(reel);
        })
        .catch(() => {});
    }
  }

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', width: '100%' }}>
      <PageHeader
        title="Highlights"
        description="Create and share your best moments"
        icon={Film}
        iconColor="var(--purple-600)"
        actions={
          <Button onClick={() => setShowCreateModal(true)} iconLeft={<Plus size={16} />}>
            New Reel
          </Button>
        }
      />

      {/* Team leaderboard — top of page, ranked by latest assessment overall_rating */}
      {org && primaryTeam && (
        <TeamLeaderboard
          orgId={org.org_id}
          teamId={primaryTeam.team_id}
          teamName={primaryTeam.name}
          currentUserId={proxyAthleteId || userId}
        />
      )}

      {/* Quick actions */}
      <div
        className="grid gap-3 mb-5"
        style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))' }}
      >
        <button
          onClick={() => setShowClipCreator(true)}
          className="card card-interactive"
          style={{ padding: 'clamp(12px, 3vw, 20px)', textAlign: 'left' }}
        >
          <div
            className="icon-container icon-container-sm"
            style={{ background: 'rgba(var(--purple-rgb), 0.1)', marginBottom: '8px' }}
          >
            <Scissors size={18} style={{ color: 'var(--purple-600)' }} />
          </div>
          <h3
            style={{
              fontWeight: '600',
              color: 'var(--gray-900)',
              marginBottom: '2px',
              fontSize: '14px',
            }}
          >
            Create Clips
          </h3>
          <p style={{ fontSize: '12px', color: 'var(--gray-500)' }}>Cut clips from videos</p>
        </button>
        <button
          onClick={() => (canUseAI ? setShowClipCreator(true) : setShowAIPaywall(true))}
          className="card card-interactive"
          style={{ padding: 'clamp(12px, 3vw, 20px)', textAlign: 'left', position: 'relative' }}
        >
          {!canUseAI && (
            <div style={{ position: 'absolute', top: '8px', right: '8px' }}>
              <PremiumBadge size="sm" />
            </div>
          )}
          <div
            className="icon-container icon-container-sm"
            style={{
              background: canUseAI
                ? 'rgba(var(--green-rgb), 0.1)'
                : 'linear-gradient(135deg, rgba(var(--purple-rgb), 0.15) 0%, rgba(236, 72, 153, 0.15) 100%)',
              marginBottom: '8px',
            }}
          >
            <Sparkles
              size={18}
              style={{ color: canUseAI ? 'var(--color-success)' : 'var(--purple-500)' }}
            />
          </div>
          <h3
            style={{
              fontWeight: '600',
              color: 'var(--gray-900)',
              marginBottom: '2px',
              fontSize: '14px',
            }}
          >
            AI Highlights
          </h3>
          <p style={{ fontSize: '12px', color: 'var(--gray-500)' }}>
            {canUseAI ? 'Find best moments' : 'Upgrade to unlock'}
          </p>
        </button>
      </div>

      {/* AI Paywall Modal */}
      {showAIPaywall && (
        <PaywallOverlay feature="ai_highlights" onClose={() => setShowAIPaywall(false)} />
      )}

      {loading ? (
        <Skeleton.Card count={4} />
      ) : reels.length === 0 ? (
        <EmptyState.Highlights onAction={() => setShowCreateModal(true)} />
      ) : (
        <TileGrid
          items={reels}
          keyExtractor={(reel) => reel.reel_id}
          renderTile={(reel) => <HighlightTile reel={reel} onClick={() => setSelectedReel(reel)} />}
          showViewToggle={false}
        />
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <CreateReelModal
          orgId={org?.org_id}
          onClose={() => setShowCreateModal(false)}
          onComplete={handleCreateComplete}
        />
      )}

      {/* Reel Detail Modal */}
      {selectedReel && org && (
        <ReelDetailModal
          reel={selectedReel}
          orgId={org.org_id}
          onClose={() => setSelectedReel(null)}
          onUpdate={handleReelUpdate}
          canPublish={canPublish}
        />
      )}

      {/* Clip Creator Modal */}
      {showClipCreator && org && (
        <ClipCreatorModal
          orgId={org.org_id}
          onClose={() => setShowClipCreator(false)}
          onClipCreated={loadReels}
          canUseAI={canUseAI}
          onShowAIPaywall={() => setShowAIPaywall(true)}
        />
      )}
    </div>
  );
}

interface CreateReelModalProps {
  orgId?: string;
  onClose: () => void;
  onComplete: () => void;
}

function CreateReelModal({ orgId, onClose, onComplete }: CreateReelModalProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleCreate() {
    if (!title) return;
    if (!orgId) {
      // The parent passes `org?.org_id` so this can be undefined when no
      // org is selected. createHighlightReel requires it; bail with a
      // user-visible error rather than firing a request to /undefined.
      setError('Pick an organization before creating a reel.');
      return;
    }

    setCreating(true);
    setError(null);

    try {
      await createHighlightReel({
        title,
        description: description || undefined,
        org_id: orgId,
      });
      onComplete();
    } catch (err) {
      console.error('Failed to create reel:', err);
      setError(err instanceof Error ? err.message : 'Failed to create reel. Please try again.');
      setCreating(false);
    }
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        className="card"
        style={{ width: '100%', maxWidth: '420px', padding: '24px' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex-between" style={{ marginBottom: '20px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: '600', color: 'var(--gray-900)' }}>
            Create Highlight Reel
          </h2>
          <Button variant="ghost" onClick={onClose} aria-label="Close">
            <X size={20} />
          </Button>
        </div>

        {error && (
          <div
            style={{
              padding: '12px 16px',
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: 'var(--radius-md)',
              color: 'var(--color-danger)',
              marginBottom: '16px',
              fontSize: '14px',
            }}
          >
            {error}
          </div>
        )}

        <div className="form-group">
          <label>Reel Title *</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g., Season Highlights 2024"
            disabled={creating}
          />
        </div>

        <div className="form-group">
          <label>Description (optional)</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Add a description for your highlight reel..."
            rows={3}
            disabled={creating}
            style={{ resize: 'vertical' }}
          />
        </div>

        <p style={{ fontSize: '13px', color: 'var(--gray-500)', marginBottom: '20px' }}>
          After creating, you can add clips from your videos to build your highlight reel.
        </p>

        <div className="flex gap-3">
          <Button variant="secondary" onClick={onClose} disabled={creating} style={{ flex: 1 }}>
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            disabled={!title}
            loading={creating}
            iconLeft={
              creating ? <Loader2 size={18} className="animate-spin" /> : <Plus size={18} />
            }
            style={{ flex: 1 }}
          >
            {creating ? 'Creating...' : 'Create Reel'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ShareModal({ reel, onClose }: { reel: HighlightReel; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const shareUrl =
    reel.share_url || `${window.location.origin}/reel/${reel.share_token || reel.reel_id}`;

  function handleCopy() {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        className="card"
        style={{ width: '100%', maxWidth: '420px', padding: '24px' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex-between" style={{ marginBottom: '20px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: '600', color: 'var(--gray-900)' }}>
            Share Highlight Reel
          </h2>
          <Button variant="ghost" onClick={onClose} aria-label="Close">
            <X size={20} />
          </Button>
        </div>

        <p style={{ fontSize: '14px', color: 'var(--gray-600)', marginBottom: '16px' }}>
          Share this link with anyone to let them view your highlight reel:
        </p>

        <div
          style={{
            display: 'flex',
            gap: '8px',
            padding: '12px',
            background: 'var(--gray-50)',
            borderRadius: 'var(--radius-md)',
            marginBottom: '20px',
          }}
        >
          <input
            type="text"
            value={shareUrl}
            readOnly
            style={{
              flex: 1,
              border: 'none',
              background: 'transparent',
              fontSize: '13px',
              color: 'var(--gray-700)',
            }}
          />
          <Button
            onClick={handleCopy}
            size="sm"
            iconLeft={copied ? <Check size={14} /> : <Copy size={14} />}
          >
            {copied ? 'Copied!' : 'Copy'}
          </Button>
        </div>

        <Button variant="secondary" onClick={onClose} fullWidth>
          Done
        </Button>
      </div>
    </div>
  );
}

interface ReelDetailModalProps {
  reel: HighlightReel;
  orgId: string;
  onClose: () => void;
  onUpdate: () => void;
  canPublish?: boolean;
}

function ReelDetailModal({
  reel,
  orgId,
  onClose,
  onUpdate,
  canPublish = false,
}: ReelDetailModalProps) {
  const [reelData, setReelData] = useState<HighlightReel>(reel);
  const [availableClips, setAvailableClips] = useState<VideoClip[]>([]);
  const [showPublishPaywall, setShowPublishPaywall] = useState(false);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [addingClip, setAddingClip] = useState<string | null>(null);
  const [removingClip, setRemovingClip] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, [reel.reel_id]);

  async function loadData() {
    setLoading(true);
    try {
      // Load reel details and available clips
      const [reelResult, videosResult] = await Promise.all([
        getHighlightReel(reel.reel_id),
        listVideos(orgId),
      ]);
      setReelData(reelResult.reel);

      // Get clips from all videos
      const allClips: VideoClip[] = [];
      for (const video of videosResult.videos || []) {
        try {
          const { clips } = await listVideoClips(video.video_id);
          allClips.push(...(clips || []));
        } catch {
          // Skip videos without clips
        }
      }
      setAvailableClips(allClips);
    } catch (err) {
      console.error('Failed to load reel data:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handlePublish() {
    setPublishing(true);
    try {
      await publishHighlightReel(reel.reel_id);
      onUpdate();
      loadData();
    } catch (err) {
      console.error('Failed to publish:', err);
      alert('Failed to publish reel.');
    } finally {
      setPublishing(false);
    }
  }

  async function handleDelete() {
    if (!confirm('Are you sure you want to delete this reel?')) return;
    setDeleting(true);
    try {
      await deleteHighlightReel(reel.reel_id);
      onUpdate();
      onClose();
    } catch (err) {
      console.error('Failed to delete:', err);
      alert('Failed to delete reel.');
      setDeleting(false);
    }
  }

  async function handleAddClip(clipId: string) {
    setAddingClip(clipId);
    try {
      // addClipToReel takes a discriminated union as its second arg —
      // `{ clip_id }` for an existing clip or `{ video_id, start_time, ... }`
      // to mint a new one inline. The previous call passed the raw clipId
      // string, which silently broke at runtime.
      await addClipToReel(reel.reel_id, { clip_id: clipId });
      loadData();
      onUpdate();
    } catch (err) {
      console.error('Failed to add clip:', err);
      alert('Failed to add clip to reel.');
    } finally {
      setAddingClip(null);
    }
  }

  async function handleRemoveClip(clipId: string) {
    setRemovingClip(clipId);
    try {
      await removeClipFromReel(reel.reel_id, clipId);
      loadData();
      onUpdate();
    } catch (err) {
      console.error('Failed to remove clip:', err);
      alert('Failed to remove clip from reel.');
    } finally {
      setRemovingClip(null);
    }
  }

  // Clips already in the reel
  const reelClipIds = new Set(reelData.clips?.map((c) => c.clip_id) || []);
  const unusedClips = availableClips.filter((c) => !reelClipIds.has(c.clip_id));

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        className="card"
        style={{
          width: '100%',
          maxWidth: '700px',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ padding: '20px', borderBottom: '1px solid var(--gray-100)' }}>
          <div className="flex-between">
            <div>
              <h2
                style={{
                  fontSize: '20px',
                  fontWeight: '600',
                  color: 'var(--gray-900)',
                  marginBottom: '4px',
                }}
              >
                {reelData.title}
              </h2>
              <p style={{ fontSize: '13px', color: 'var(--gray-500)' }}>
                {reelData.clip_count || 0} clips • {formatDuration(reelData.total_duration || 0)}
              </p>
            </div>
            <Button variant="ghost" onClick={onClose} aria-label="Close">
              <X size={20} />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '20px' }}>
          {loading ? (
            <div className="flex-center" style={{ padding: '40px' }}>
              <div
                className="animate-spin"
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  border: '3px solid var(--gray-200)',
                  borderTopColor: 'var(--purple-600)',
                }}
              />
            </div>
          ) : (
            <>
              {/* Clips in Reel */}
              <div style={{ marginBottom: '24px' }}>
                <h3
                  style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: 'var(--gray-800)',
                    marginBottom: '12px',
                  }}
                >
                  Clips in Reel ({reelData.clips?.length || 0})
                </h3>
                {reelData.clips && reelData.clips.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {reelData.clips.map((clip, index) => (
                      <div
                        key={clip.clip_id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '12px',
                          background: 'var(--gray-50)',
                          borderRadius: 'var(--radius-md)',
                        }}
                      >
                        <div
                          style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '6px',
                            background: 'var(--purple-600)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontWeight: '600',
                            fontSize: '14px',
                          }}
                        >
                          {index + 1}
                        </div>
                        <div style={{ flex: 1 }}>
                          <p
                            style={{
                              fontWeight: '500',
                              color: 'var(--gray-900)',
                              fontSize: '14px',
                            }}
                          >
                            {clip.title || clip.video_title || 'Untitled Clip'}
                          </p>
                          <p style={{ fontSize: '12px', color: 'var(--gray-500)' }}>
                            {formatDuration(Math.max(0, clip.end_time - clip.start_time))}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          onClick={() => clip.clip_id && handleRemoveClip(clip.clip_id)}
                          disabled={!clip.clip_id}
                          loading={removingClip === clip.clip_id}
                          aria-label="Remove clip"
                          style={{ color: 'var(--color-danger)' }}
                        >
                          {removingClip === clip.clip_id ? (
                            <Loader2 size={16} className="animate-spin" />
                          ) : (
                            <Trash2 size={16} />
                          )}
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div
                    style={{
                      textAlign: 'center',
                      padding: '20px',
                      color: 'var(--gray-500)',
                      background: 'var(--gray-50)',
                      borderRadius: 'var(--radius-md)',
                    }}
                  >
                    <Film size={24} style={{ marginBottom: '8px', opacity: 0.5 }} />
                    <p style={{ fontSize: '14px' }}>No clips in this reel yet</p>
                  </div>
                )}
              </div>

              {/* Available Clips to Add */}
              {unusedClips.length > 0 && (
                <div>
                  <h3
                    style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: 'var(--gray-800)',
                      marginBottom: '12px',
                    }}
                  >
                    Available Clips ({unusedClips.length})
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {unusedClips.map((clip) => (
                      <div
                        key={clip.clip_id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '12px',
                          background: 'var(--gray-50)',
                          borderRadius: 'var(--radius-md)',
                        }}
                      >
                        <div
                          style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '6px',
                            background:
                              'linear-gradient(135deg, var(--primary-start) 0%, var(--primary-end) 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Play size={16} style={{ color: 'white' }} />
                        </div>
                        <div style={{ flex: 1 }}>
                          <p
                            style={{
                              fontWeight: '500',
                              color: 'var(--gray-900)',
                              fontSize: '14px',
                            }}
                          >
                            {clip.title || clip.video_title || 'Untitled Clip'}
                          </p>
                          <p style={{ fontSize: '12px', color: 'var(--gray-500)' }}>
                            {formatDuration(clip.duration || 0)}
                            {clip.source === 'AI_DETECTED' && (
                              <span style={{ marginLeft: '8px', color: 'var(--color-success)' }}>
                                <Sparkles
                                  size={10}
                                  style={{ display: 'inline', marginRight: '2px' }}
                                />
                                AI detected
                              </span>
                            )}
                          </p>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => handleAddClip(clip.clip_id)}
                          loading={addingClip === clip.clip_id}
                          iconLeft={
                            addingClip === clip.clip_id ? (
                              <Loader2 size={14} className="animate-spin" />
                            ) : (
                              <Plus size={14} />
                            )
                          }
                        >
                          Add
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {unusedClips.length === 0 && availableClips.length === 0 && (
                <div
                  style={{
                    textAlign: 'center',
                    padding: '20px',
                    color: 'var(--gray-500)',
                    background: 'var(--gray-50)',
                    borderRadius: 'var(--radius-md)',
                  }}
                >
                  <Scissors size={24} style={{ marginBottom: '8px', opacity: 0.5 }} />
                  <p style={{ fontSize: '14px' }}>
                    No clips available. Create clips from your videos first!
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Actions */}
        <div
          style={{
            padding: '16px 20px',
            borderTop: '1px solid var(--gray-100)',
            display: 'flex',
            gap: '12px',
          }}
        >
          <Button
            variant="ghost"
            onClick={handleDelete}
            loading={deleting}
            iconLeft={
              deleting ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />
            }
            style={{ color: 'var(--color-danger)' }}
          >
            Delete
          </Button>
          <div style={{ flex: 1 }} />
          {reelData.status === 'PUBLISHED' ? (
            <Button onClick={() => setShowShareModal(true)} iconLeft={<Share2 size={16} />}>
              Share
            </Button>
          ) : canPublish ? (
            <Button
              onClick={handlePublish}
              disabled={(reelData.clips?.length || 0) === 0}
              loading={publishing}
              iconLeft={
                publishing ? <Loader2 size={16} className="animate-spin" /> : <Share2 size={16} />
              }
            >
              Publish
            </Button>
          ) : (
            <button
              onClick={() => setShowPublishPaywall(true)}
              disabled={(reelData.clips?.length || 0) === 0}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                background: 'linear-gradient(135deg, var(--purple-500) 0%, var(--purple-600) 100%)',
                color: 'white',
              }}
            >
              <Crown size={16} />
              Upgrade to Publish
            </button>
          )}
        </div>

        {showShareModal && <ShareModal reel={reelData} onClose={() => setShowShareModal(false)} />}

        {showPublishPaywall && (
          <PaywallOverlay feature="publish_reels" onClose={() => setShowPublishPaywall(false)} />
        )}
      </div>
    </div>
  );
}

interface ClipCreatorModalProps {
  orgId: string;
  onClose: () => void;
  onClipCreated: () => void;
  canUseAI?: boolean;
  onShowAIPaywall?: () => void;
}

function ClipCreatorModal({
  orgId,
  onClose,
  onClipCreated,
  canUseAI = false,
  onShowAIPaywall,
}: ClipCreatorModalProps) {
  const [videos, setVideos] = useState<Video[]>([]);
  const [selectedVideo, setSelectedVideo] = useState<Video | null>(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [aiDetecting, setAiDetecting] = useState(false);
  const [clipStart, setClipStart] = useState('0');
  const [clipEnd, setClipEnd] = useState('10');
  const [clipTitle, setClipTitle] = useState('');

  useEffect(() => {
    loadVideos();
  }, []);

  async function loadVideos() {
    setLoading(true);
    try {
      const { videos: videoList } = await listVideos(orgId);
      // Only show ready videos
      setVideos((videoList || []).filter((v) => v.status === 'READY'));
    } catch (err) {
      console.error('Failed to load videos:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleCreateClip() {
    if (!selectedVideo) return;

    const start = parseFloat(clipStart) || 0;
    const end = parseFloat(clipEnd) || 10;

    if (end <= start) {
      alert('End time must be greater than start time');
      return;
    }

    setCreating(true);
    try {
      // createClip(videoId, { start_time, end_time, title?, description? })
      // Was passing the old object-shape with video_id/org_id inline; the
      // function takes the videoId as the first positional arg and only
      // accepts start/end/title/description in the data object.
      void orgId; // Reserved for future org-scoping; backend infers from videoId today.
      await createClip(selectedVideo.video_id, {
        start_time: start,
        end_time: end,
        title: clipTitle || undefined,
      });
      onClipCreated();
      setClipTitle('');
      setClipStart('0');
      setClipEnd('10');
      alert('Clip created successfully!');
    } catch (err) {
      console.error('Failed to create clip:', err);
      alert('Failed to create clip. Please try again.');
    } finally {
      setCreating(false);
    }
  }

  async function handleAIDetect() {
    if (!selectedVideo) return;

    setAiDetecting(true);
    try {
      const result = await aiDetectHighlights(selectedVideo.video_id);
      const clipCount = result.clips?.length || 0;
      alert(`AI detected ${clipCount} highlights! They've been added to your available clips.`);
      onClipCreated();
    } catch (err) {
      console.error('Failed to detect highlights:', err);
      alert('Failed to detect highlights. Please try again.');
    } finally {
      setAiDetecting(false);
    }
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        className="card"
        style={{
          width: '100%',
          maxWidth: 'min(500px, calc(100vw - 32px))',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ padding: '20px', borderBottom: '1px solid var(--gray-100)' }}>
          <div className="flex-between">
            <h2 style={{ fontSize: '20px', fontWeight: '600', color: 'var(--gray-900)' }}>
              Create Clips
            </h2>
            <Button variant="ghost" onClick={onClose} aria-label="Close">
              <X size={20} />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '20px' }}>
          {loading ? (
            <div className="flex-center" style={{ padding: '40px' }}>
              <div
                className="animate-spin"
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  border: '3px solid var(--gray-200)',
                  borderTopColor: 'var(--purple-600)',
                }}
              />
            </div>
          ) : videos.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-500)' }}>
              <VideoIcon size={32} style={{ marginBottom: '12px', opacity: 0.5 }} />
              <p>No videos available. Upload videos first to create clips!</p>
            </div>
          ) : (
            <>
              {/* Video Selection */}
              <div className="form-group">
                <label>Select Video</label>
                <select
                  value={selectedVideo?.video_id || ''}
                  onChange={(e) =>
                    setSelectedVideo(videos.find((v) => v.video_id === e.target.value) || null)
                  }
                >
                  <option value="">Choose a video...</option>
                  {videos.map((video) => (
                    <option key={video.video_id} value={video.video_id}>
                      {video.title}
                    </option>
                  ))}
                </select>
              </div>

              {selectedVideo && (
                <>
                  {/* AI Detect Button */}
                  <button
                    onClick={() => {
                      if (canUseAI) {
                        handleAIDetect();
                      } else {
                        onClose();
                        onShowAIPaywall?.();
                      }
                    }}
                    disabled={aiDetecting}
                    className="btn-secondary"
                    style={{
                      width: '100%',
                      marginBottom: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      background: !canUseAI
                        ? 'linear-gradient(135deg, rgba(var(--purple-rgb), 0.1) 0%, rgba(236, 72, 153, 0.1) 100%)'
                        : undefined,
                      borderColor: !canUseAI ? 'rgba(var(--purple-rgb), 0.3)' : undefined,
                    }}
                  >
                    {aiDetecting ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : !canUseAI ? (
                      <Lock size={16} style={{ color: 'var(--purple-500)' }} />
                    ) : (
                      <Sparkles size={16} />
                    )}
                    {aiDetecting
                      ? 'Detecting...'
                      : !canUseAI
                        ? 'Upgrade to Use AI Detection'
                        : 'AI Auto-Detect Highlights'}
                  </button>

                  <div
                    style={{
                      textAlign: 'center',
                      color: 'var(--gray-400)',
                      margin: '16px 0',
                      fontSize: '13px',
                    }}
                  >
                    — or create manually —
                  </div>

                  {/* Manual Clip Creation */}
                  <div className="form-group">
                    <label>Clip Title (optional)</label>
                    <input
                      type="text"
                      value={clipTitle}
                      onChange={(e) => setClipTitle(e.target.value)}
                      placeholder="e.g., Best play of the game"
                    />
                  </div>

                  <div style={{ display: 'flex', gap: '12px' }}>
                    <div className="form-group" style={{ flex: 1 }}>
                      <label>Start (seconds)</label>
                      <input
                        type="number"
                        value={clipStart}
                        onChange={(e) => setClipStart(e.target.value)}
                        min="0"
                        step="0.5"
                      />
                    </div>
                    <div className="form-group" style={{ flex: 1 }}>
                      <label>End (seconds)</label>
                      <input
                        type="number"
                        value={clipEnd}
                        onChange={(e) => setClipEnd(e.target.value)}
                        min="0"
                        step="0.5"
                      />
                    </div>
                  </div>

                  <Button
                    onClick={handleCreateClip}
                    loading={creating}
                    fullWidth
                    iconLeft={
                      creating ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <Scissors size={16} />
                      )
                    }
                    style={{ marginTop: '8px' }}
                  >
                    {creating ? 'Creating...' : 'Create Clip'}
                  </Button>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
